import { type DrawerProps, Box, Drawer, List, styled } from '@shared/components';
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useSideBar } from '../../contexts';
import type { MenuItem } from '../../domains';
import { MENU_DRAWER_WIDTH_LARGE } from '../../utils/constants';
import { RelativeMenuItem } from '../MenuItem';

import SecondarySideBarHeader from './SecondarySideBarHeader';
import SecondarySubMenu from './SecondarySubMenu';

type SecondaryMenuProps = {
  subMenuTitle?: string;
} & MenuItem;

interface SecondarySideBarProps {
  menuTitle?: string;
  secondaryMenu: SecondaryMenuProps[];
}

export const SideBar = styled(Drawer, {
  name: 'SideBar',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles['root']],
})<DrawerProps>({
  '.MuiPaper-root': {
    position: 'relative',
    width: MENU_DRAWER_WIDTH_LARGE,
  },
});

function SecondarySideBar(props: Readonly<SecondarySideBarProps>) {
  const { menuTitle, secondaryMenu } = props;
  const { setIsSecondarySideBarMounted } = useSideBar();

  const { pathname: currentPath } = useLocation();

  const selectedMenu = secondaryMenu.find((item) => {
    const isOnParentMenuOrSameSubMenu = matchPath(
      { path: item.link as string, end: false },
      currentPath,
    );
    return isOnParentMenuOrSameSubMenu;
  });

  const subMenu = selectedMenu?.subMenu;

  // automatically minimize primary sidebar on mount
  useEffect(() => {
    setIsSecondarySideBarMounted(true);
    return () => setIsSecondarySideBarMounted(false);
  }, []);

  return (
    <SideBar variant='permanent' open className='secondary'>
      <SecondarySideBarHeader menuTitle={menuTitle} />
      <List>
        {secondaryMenu?.map((item) => <RelativeMenuItem key={item.label as string} {...item} />)}
      </List>
      <Box pb={4} />
      {subMenu ? <SecondarySubMenu title={selectedMenu.subMenuTitle} subMenu={subMenu} /> : null}
      <Box id='mini-content-menu-container' sx={{ height: '100%', overflowY: 'hidden' }} />
    </SideBar>
  );
}

export type { SecondaryMenuProps, SecondarySideBarProps };
export default SecondarySideBar;
